import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddClientResponse,
  CheckAccountStatusRequest,
  CheckBinanceAccountStatus,
  CreateAccessTokenRequest,
  CreateAccessTokenResponse,
  CreateApiKeysRequest,
  LanguageNameSpaces,
  OpenMessage,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  BINANCE_API_MANAGEMENT_LINK,
  BYBIT_API_MANAGEMENT_LINK,
  KUCOIN_API_MANAGEMENT_LINK,
  MEXC_API_MANAGEMENT_LINK,
  OKX_API_MANAGEMENT_LINK,
  OKX_EU_API_MANAGEMENT_LINK,
  PRIMARY_EXCHANGES,
} from "@shared/common/constants";
import useDelayedLoading from "@shared/features/onboarding/hooks/useDelayedLoading";
import {
  CREATE_ACCOUNT_ERROR_DEFAULT_TITLE,
  QUICK_CONNECT_API_EXISTS_ERROR_CODE,
  QUICK_CONNECT_API_TERMS_ERROR_CODE,
  QUICK_CONNECT_API_TERMS_ERROR_PATTERN,
} from "@shared/features/onboarding/constants";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import useUrlQuickConnect from "@shared/features/onboarding/hooks/useUrlQuickConnect";
import { selectIsProcessingQuickConnect } from "@shared/slices/common.slice";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { UrlQuickConnectQueryData } from "@shared/features/onboarding/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useQuickConnectProviderProps {
  queryCode?: string | null;
  queryState?: string | null;
  queryData: UrlQuickConnectQueryData;
  onInitializeCodeAndState?: () => void;
  openMessage: OpenMessage;
  isOnboarding?: boolean;
  onOpenModal?: () => void;
  onCloseModal?: () => void;
  onSubmitSuccess?: () => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  onFixableOnExchangeError?: (description: string) => void;
  onCreateAccessToken?: (primaryExchange: string) => void;
  pollingAccountCreated?: boolean;
  isMobileApp?: boolean;
}

const useQuickConnectProvider = ({
  queryCode,
  queryState,
  queryData,
  onInitializeCodeAndState,
  openMessage,
  isOnboarding,
  onOpenModal,
  onCloseModal,
  onSubmitSuccess,
  getSuccessMessage,
  getErrorNotification,
  onFixableOnExchangeError,
  onCreateAccessToken: propsOnCreateAccessToken,
  pollingAccountCreated,
  isMobileApp,
}: useQuickConnectProviderProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const dispatch = useDispatch();
  const { isExternalConnectAccount, token: externalInviteToken } =
    useContext(ExternalConnectAccountContext) || {};
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const isProcessingQuickConnect = useSelector(selectIsProcessingQuickConnect);

  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [hasMatchAccount, setHasMatchAccount] = useState<boolean>();
  const [createApiKeysFailed, setCreateApiKeysFailed] = useState<number>();
  const [code, setCode] = useState<string | null>();
  const [state, setState] = useState<string | null>();
  const [exchangeToClientName, setExchangeToClientName] =
    useState<Record<string, string>>();
  const [createApiKeysErrorMessage, setCreateApiKeysErrorMessage] =
    useState<string>();
  const [isInitialized, setIsInitialized] = useState(!queryState || !queryCode);
  const [quickConnectSucceeded, setQuickConnectSucceeded] = useState(false);
  const { exchangeToClientName: formExchangeToClientName } =
    useContext(AccountFormContext)!;

  const {
    createAccountDrawerOpen,
    openCreateAccountDrawer,
    closeCreateAccountDrawer,
    resetFormValues,
    selectedSubExchanges,
    setSelectedSubExchanges,
    setAccountName,
    getSubExchanges,
    exchangesData,
    setCreatedAccounts,
  } = useContext(AccountFormContext)!;

  const closeDrawer = () => {
    if (!isOnboarding) {
      closeCreateAccountDrawer();
      resetQuickConnectState();
      resetFormValues();
      onCloseModal?.();
    }
    dispatch(
      sharedLib.slices.common.actions.setIsProcessingQuickConnect(false)
    );
  };

  useUrlQuickConnect({
    queryData,
    openMessage,
    onSubmitSuccess,
    closeDrawer,
    getSuccessMessage,
    getErrorNotification,
    isOnboarding,
    isMobileApp,
  });

  const [
    createAccessToken,
    { data: createAccessTokenResponse, isLoading: createAccessTokenLoading },
  ] = sharedLib.sharedApi.configuration.useCreateAccessTokenMutation();

  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();

  const primaryExchange = createAccessTokenResponse?.nested_exchange;

  const [
    checkAccountStatus,
    {
      data: binanceAccountStatus,
      isLoading: checkAccountStatusLoading,
      isFetching: checkAccountStatusFetching,
    },
  ] = sharedLib.sharedApi.configuration.useLazyCheckAccountStatusQuery();

  const [
    createApiKeys,
    { isLoading: createApiKeysLoading, isSuccess: createApiKeysSuccess },
  ] = sharedLib.sharedApi.configuration.useCreateApiKeysMutation();

  const loading =
    createAccessTokenLoading ||
    checkAccountStatusLoading ||
    createApiKeysLoading;
  const isLoading = useDelayedLoading(loading, 100);
  const onboardingCallbackPending =
    isOnboarding &&
    !!(code || queryCode) &&
    !!(state || queryState) &&
    !quickConnectSucceeded &&
    hasMatchAccount !== false;
  const quickConnectLoading =
    (isLoading || !!onboardingCallbackPending) && !createApiKeysFailed;

  const onCreateAccessToken = (response: CreateAccessTokenResponse) => {
    if (!state) return;
    const {
      nested_exchange: primaryExchange,
      exchange_to_client_name: exchangeToClientNameResponse,
      client_base_name: accountName,
    } = response;
    propsOnCreateAccessToken?.(primaryExchange);
    setExchangeToClientName(exchangeToClientNameResponse);
    setAccountName(accountName);

    if (primaryExchange === PRIMARY_EXCHANGES.BINANCE) {
      const args: CheckAccountStatusRequest = { state };
      if (externalInviteToken) {
        args.authToken = externalInviteToken;
      }
      checkAccountStatus(args);
    } else if (
      [
        PRIMARY_EXCHANGES.OKX,
        PRIMARY_EXCHANGES.OKX_EU,
        PRIMARY_EXCHANGES.BYBIT,
        PRIMARY_EXCHANGES.KUCOIN,
        PRIMARY_EXCHANGES.BINGX,
        PRIMARY_EXCHANGES.MEXC,
      ].includes(primaryExchange)
    ) {
      setHasMatchAccount(true);
    }
    setHasAccessToken(true);
  };

  const createAccessTokenHandler = () => {
    if (!code || !state) return;
    const args: CreateAccessTokenRequest = { code, state };
    if (externalInviteToken) {
      args.authToken = externalInviteToken;
    }

    createAccessToken(args)
      .unwrap()
      .then((response) => onCreateAccessToken(response))
      .catch((error) => {
        openMessage(error?.data?.message, "error");
        handleFetchPortfolioData();
        if (isOnboarding) resetCodeAndState();
        setIsInitialized(true);
      });
  };

  const binanceSelectedSubExchangesValid = useCallback(() => {
    return !selectedSubExchanges?.some(
      (subExchange) =>
        binanceAccountStatus?.[
          subExchange.exchange as keyof CheckBinanceAccountStatus
        ] === false
    );
  }, [selectedSubExchanges, binanceAccountStatus]);

  const handleFetchPortfolioData = useCallback(() => {
    if (!isProcessingQuickConnect) {
      try {
        getPortfolioData(undefined, false);
      } catch {}
    } else {
      dispatch(
        sharedLib.slices.common.actions.setIsProcessingQuickConnect(false)
      );
    }
  }, [isProcessingQuickConnect]);

  useEffect(() => {
    if (!code && !state && queryCode && queryState) {
      if (!isProcessingQuickConnect) {
        dispatch(
          sharedLib.slices.common.actions.setIsProcessingQuickConnect(true)
        );
      }
      setCode(queryCode);
      setState(queryState);
      onInitializeCodeAndState?.();
    } else if (
      !queryCode &&
      !queryState &&
      isProcessingQuickConnect === undefined
    ) {
      dispatch(
        sharedLib.slices.common.actions.setIsProcessingQuickConnect(false)
      );
    }
  }, [queryCode, queryState]);

  useEffect(() => {
    createAccessTokenHandler();
  }, [code, state]);

  useEffect(() => {
    if (exchangeToClientName && exchangesData) {
      setSelectedSubExchanges(getSubExchanges(exchangeToClientName));
    }
  }, [exchangeToClientName, exchangesData]);

  useEffect(() => {
    if (binanceAccountStatus) {
      setHasMatchAccount(binanceSelectedSubExchangesValid());
    }
  }, [binanceAccountStatus]);

  useEffect(() => {
    if (hasMatchAccount) {
      createApiKeysHandler();
    } else if (hasMatchAccount === false) {
      handleFetchPortfolioData();
    }
  }, [hasMatchAccount]);

  useEffect(() => {
    if (
      code &&
      state &&
      binanceAccountStatus &&
      hasMatchAccount === false &&
      !createAccountDrawerOpen
    ) {
      openDrawer();
    }
  }, [
    binanceAccountStatus,
    hasMatchAccount,
    code,
    state,
    createAccountDrawerOpen,
  ]);

  useEffect(() => {
    if (
      (createApiKeysSuccess || pollingAccountCreated) &&
      (user?.has_clients_connected || isExternalConnectAccount)
    ) {
      if (isOnboarding) {
        resetCodeAndState();
        setQuickConnectSucceeded(true);
      }
      onSubmitSuccess?.();
    }
  }, [
    createApiKeysSuccess,
    user?.has_clients_connected,
    pollingAccountCreated,
    isExternalConnectAccount,
  ]);

  const resetCodeAndState = () => {
    setCode(undefined);
    setState(undefined);
  };

  const resetQuickConnectState = () => {
    resetCodeAndState();
    setHasAccessToken(false);
    setCreateApiKeysFailed(undefined);
    setHasMatchAccount(undefined);
  };

  const openDrawer = () => {
    if (!isOnboarding) {
      openCreateAccountDrawer();
      onOpenModal?.();
    }
  };

  const getIsAPIKeysExistedError = (code?: number, response?: string) => {
    return (
      code === QUICK_CONNECT_API_EXISTS_ERROR_CODE &&
      response?.toLowerCase().includes("api keys")
    );
  };

  // Fixed on the exchange's site while the authorization stays valid, so
  // the same state can create the keys once the user comes back.
  const getIsFixableOnExchangeError = (code?: number, response?: string) =>
    getIsAPIKeysExistedError(code, response) ||
    getIsAPITermsError(code, response);

  const createApiKeysHandler = useCallback(() => {
    if (!state || !exchangeToClientName) return;
    const exchangeToClientNameMap =
      !!formExchangeToClientName && Object.keys(formExchangeToClientName).length
        ? formExchangeToClientName
        : exchangeToClientName;
    const args: CreateApiKeysRequest = {
      state,
      exchange_to_client_name: exchangeToClientNameMap,
    };
    if (externalInviteToken) {
      args.authToken = externalInviteToken;
    }
    createApiKeys(args)
      .unwrap()
      .then((response: AddClientResponse) => {
        closeDrawer();
        setCreatedAccounts(response.data.data);
        if (!isExternalConnectAccount) getSuccessMessage?.(response);
      })
      .catch((error) => {
        setCreateApiKeysFailed(error.status);
        if (getIsFixableOnExchangeError(error.status, error.data.message)) {
          setCreateApiKeysErrorMessage(error.data.message);
          onFixableOnExchangeError?.(error.data.message);
        } else {
          resetCodeAndState();
          if (error.status === 400 || error.status === 409) {
            getErrorNotification?.(
              error?.data?.message,
              error.data.title ?? t(CREATE_ACCOUNT_ERROR_DEFAULT_TITLE)
            );
          } else {
            openMessage(error?.data?.message, "error");
          }
        }
      })
      .finally(() => {
        if (!isExternalConnectAccount) {
          handleFetchPortfolioData();
        }
      });
  }, [
    state,
    exchangeToClientName,
    formExchangeToClientName,
    handleFetchPortfolioData,
    isExternalConnectAccount,
  ]);

  const tryAgainLoading = checkAccountStatusFetching || createApiKeysLoading;
  const showNoFuturesAccountMessage =
    primaryExchange === PRIMARY_EXCHANGES.BINANCE &&
    hasAccessToken &&
    hasMatchAccount === false &&
    !createApiKeysFailed;

  const onNoFuturesTryAgain = useCallback(() => {
    if (binanceSelectedSubExchangesValid()) {
      createApiKeysHandler();
    } else {
      state && checkAccountStatus({ state });
    }
  }, [binanceSelectedSubExchangesValid, createApiKeysHandler, state]);

  const isAPITermsError = getIsAPITermsError(
    createApiKeysFailed,
    createApiKeysErrorMessage
  );
  const showQuickConnectAlreadyUsedWarning =
    getIsFixableOnExchangeError(
      createApiKeysFailed,
      createApiKeysErrorMessage
    ) &&
    !!primaryExchange &&
    (
      [
        PRIMARY_EXCHANGES.BINANCE,
        PRIMARY_EXCHANGES.OKX,
        PRIMARY_EXCHANGES.OKX_EU,
        PRIMARY_EXCHANGES.BYBIT,
        PRIMARY_EXCHANGES.KUCOIN,
        PRIMARY_EXCHANGES.BINGX,
        PRIMARY_EXCHANGES.MEXC,
      ] as string[]
    ).includes(primaryExchange);

  const noFuturesAccountMessageSharedData = useMemo(
    () => ({
      show: showNoFuturesAccountMessage,
      errorMessage: `You don’t have a futures trading account.`,
      onSubmit: onNoFuturesTryAgain,
      loading: tryAgainLoading,
    }),
    [showNoFuturesAccountMessage, tryAgainLoading, onNoFuturesTryAgain]
  );

  const quickConnectAlreadyUsedMessageSharedData = useMemo(
    () => ({
      show: showQuickConnectAlreadyUsedWarning,
      errorMessage:
        createApiKeysErrorMessage ??
        "Only one set of API keys can be created per account using the quick connect.",
      isAPITermsError,
      onSubmit: createApiKeysHandler,
      loading: createApiKeysLoading,
      apiManagementLink: EXCHANGE_TO_API_MANAGEMENT_LINK[primaryExchange || ""],
    }),
    [
      showQuickConnectAlreadyUsedWarning,
      isAPITermsError,
      createApiKeysErrorMessage,
      createApiKeysHandler,
      createApiKeysLoading,
      primaryExchange,
    ]
  );

  const onBackToSelectExchange = () => {
    dispatch(
      sharedLib.slices.common.actions.setIsProcessingQuickConnect(false)
    );
    resetFormValues();
    resetQuickConnectState();
  };

  return {
    noFuturesAccountMessageSharedData,
    quickConnectAlreadyUsedMessageSharedData,
    primaryExchange,
    closeDrawer,
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    onBackToSelectExchange,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  };
};

export default useQuickConnectProvider;

const getIsAPITermsError = (code?: number, response?: string) =>
  code === QUICK_CONNECT_API_TERMS_ERROR_CODE &&
  QUICK_CONNECT_API_TERMS_ERROR_PATTERN.test(response ?? "");

const EXCHANGE_TO_API_MANAGEMENT_LINK = {
  [PRIMARY_EXCHANGES.BINANCE]: BINANCE_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.OKX]: OKX_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.OKX_EU]: OKX_EU_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.BYBIT]: BYBIT_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.KUCOIN]: KUCOIN_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.MEXC]: MEXC_API_MANAGEMENT_LINK,
};
